import { designsAPI } from '../lib/api'
import type { FunnelPlanRow } from './contentFunnelSelection.types'
import type {
  SeoPipelineReadinessContext,
  SeoPipelineReadinessMissing,
} from '../features/designs/api'
import { marketplaceSupports, normalizeMarketplace, type MarketplaceCode } from '../shared/marketplaces'
import { deriveAudienceAttributes } from '../features/attributes/themeAttributes'

/**
 * Канал публикации мастера: код площадки из реестра плюс `site` — у сайта нет
 * транспорта площадки, поэтому реестр его не знает (см. `shared/marketplaces.ts`),
 * но кабинет-продавец (`site_pt`) есть, и он — цель прогона наравне с остальными.
 */
export type PipelineMarketplace = MarketplaceCode | 'site'

/**
 * Сценарии обработки после создания макетов.
 * `designs_and_products` — промежуточный: товары и файлы печати создаются,
 * визуалы и карточки маркетплейсов — нет.
 */
export type WizardPipelineMode = 'designs_only' | 'designs_and_products' | 'full_pipeline'

/** File → base64 without the data URL prefix. Shared by create and replace flows. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Превью по уже загруженному файлу: в запросе уходит только upload_id.
 * Пустая строка — миниатюру собрать не удалось.
 */
export async function renderPreviewByUpload(uploadId: string): Promise<string> {
  try {
    const res = await designsAPI.renderPdfThumbByUpload(uploadId, 400)
    const data = res.data?.data ?? res.data
    return data?.base64 ? `data:${data.mime || 'image/jpeg'};base64,${data.base64}` : ''
  } catch (error) {
    console.error('renderPdfThumbByUpload failed', error)
    return ''
  }
}

/** Legacy: превью из base64 самого файла. Остаётся для замены исходника макета. */
export async function renderPreviewServer(file: File): Promise<string> {
  try {
    const b64 = await fileToBase64(file)
    if (!b64) return ''
    const res = await designsAPI.renderPdfThumb(b64, 400)
    const data = res.data?.data ?? res.data
    return data?.base64 ? `data:${data.mime || 'image/jpeg'};base64,${data.base64}` : ''
  } catch (error) {
    console.error('renderPdfThumb failed', error)
    return ''
  }
}

/** Режимы, где бэкенд запускает конвейер по товарам (design_pipeline_runs). */
export function launchesPipelineRun(mode: string): boolean {
  return mode === 'designs_and_products' || mode === 'full_pipeline'
}

/** Режимы, требующие выбранных кабинетов маркетплейсов. */
export function requiresMarketplaceTargets(mode: string): boolean {
  return mode === 'full_pipeline'
}

/**
 * Обязательные атрибуты тематики (шаг «Характеристики»). Бренд и тематика —
 * одна обязательность на двоих: поля наследуют значения друг друга, поэтому
 * достаточно заполнить любое.
 */
export function missingCommonFieldLabels(common: {
  category?: string
  theme?: string
  brand_ru?: string
  gender?: string
  age?: string
  occasion?: string
  recipient_statuses?: string[]
  personas?: string[]
}): string[] {
  const missing: string[] = []
  if (!String(common.category || '').trim()) missing.push('Категория')
  if (!String(common.theme || '').trim() && !String(common.brand_ru || '').trim()) {
    missing.push('Тематика или Бренд')
  }
  if (!String(common.gender || '').trim()) missing.push('Пол аудитории')
  if (!String(common.age || '').trim()) missing.push('Возраст аудитории')
  if (!String(common.occasion || '').trim()) missing.push('Повод')
  if ((common.recipient_statuses || []).length === 0) missing.push('Статус получателя')
  if ((common.personas || []).length === 0) missing.push('Персона')
  return missing
}

export const WIZARD_MOBILE_CTA_CLASS =
  'col-span-2 min-w-0 w-full whitespace-normal break-words leading-tight sm:ml-auto sm:w-auto'

export const PIPELINE_TARGET_VALIDATION_SUCCESS =
  'Доступность и права выбранных кабинетов проверены. Совместимость контент-воронок будет проверена перед запуском.'

function formatBackendDetails(details: unknown): string | null {
  if (details === undefined || details === null || details === '') return null
  if (typeof details === 'string') return details
  try {
    return JSON.stringify(details)
  } catch {
    return 'детали ответа не удалось отобразить'
  }
}

/**
 * Потолок на ОДИН макет. Суммарного лимита на запуск больше нет: файлы уходят
 * потоком по одному (`uploadDesignSource`), в теле создания остаются только
 * upload_id, и набор целиком нигде не собирается в строку.
 *
 * До перехода на потоковую загрузку здесь стоял лимит на сумму, и он был
 * вынужденным: PDF ехали base64 внутри одного JSON, а такая строка упирается в
 * `String::kMaxLength` (512,0 МиБ) — в браузере и, после пересылки по шине,
 * ещё раз в Node. Теперь ограничивает только nginx, и то на файл.
 *
 * Значение совпадает с DESIGN_UPLOAD_MAX_BYTES гейтвея: самый тяжёлый PDF
 * боевого каталога — 98 МБ, средний — 25,7 МБ.
 */
export const MAX_DESIGN_UPLOAD_BYTES = 100 * 1024 * 1024

/** Человекочитаемый размер: «14,8 МБ», «295 МБ». */
export function formatUploadSize(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  if (mb >= 100) return `${Math.round(mb)} МБ`
  return `${mb.toFixed(1).replace('.', ',')} МБ`
}

export function totalUploadBytes(files: Array<{ size?: number }>): number {
  return files.reduce((sum, file) => sum + (Number(file?.size) || 0), 0)
}

/** Файлы тяжелее потолка — по именам, чтобы человек знал, какие убрать. */
export function oversizeUploadFileNames(
  files: Array<{ name?: string; size?: number }>,
): string[] {
  return files
    .filter((file) => (Number(file?.size) || 0) > MAX_DESIGN_UPLOAD_BYTES)
    .map((file) => String(file?.name || 'файл без имени'))
}

/**
 * Проверка ДО отправки. Ловит перевес на нашей стороне: иначе его поймает nginx
 * ответом 413, а он отдаёт HTML вместо JSON бэкенда — сообщение вырождалось в
 * безадресное «Повторите попытку». Возвращает null, если набор проходит.
 */
export function uploadSizeErrorMessage(
  files: Array<{ name?: string; size?: number }>,
): string | null {
  const oversize = oversizeUploadFileNames(files)
  if (oversize.length === 0) return null
  return (
    `Лимит на один макет — ${formatUploadSize(MAX_DESIGN_UPLOAD_BYTES)}. `
    + `Превышают: ${oversize.join(', ')}.`
  )
}

export function isBulkCreateOutcomeUnknown(error: unknown): boolean {
  const candidate = error as {
    code?: unknown
    message?: unknown
    request?: unknown
    response?: {
      status?: unknown
      data?: {
        error?: {
          code?: unknown
        }
      }
    }
  }
  const code = typeof candidate?.code === 'string' ? candidate.code.toUpperCase() : ''
  const message = typeof candidate?.message === 'string' ? candidate.message : ''
  const responseStatus = Number(candidate?.response?.status)
  const backendCode = typeof candidate?.response?.data?.error?.code === 'string'
    ? candidate.response.data.error.code.toUpperCase()
    : ''
  return (
    code === 'ECONNABORTED'
    || code === 'ETIMEDOUT'
    || code === 'SERVICE_TIMEOUT'
    || code === 'GATEWAY_TIMEOUT'
    || backendCode === 'SERVICE_TIMEOUT'
    || backendCode === 'GATEWAY_TIMEOUT'
    || responseStatus === 504
    || /timeout/i.test(message)
    || Boolean(candidate?.request && !candidate?.response)
  )
}

export function bulkCreateErrorMessage(error: unknown, jobId?: string): string {
  const candidate = error as {
    code?: unknown
    message?: unknown
    request?: unknown
    response?: {
      status?: unknown
      data?: {
        error?: {
          message?: unknown
          code?: unknown
          details?: unknown
        }
      }
    }
  }
  const backendError = candidate?.response?.data?.error

  // 413 приходит от nginx, а не от бэкенда: тело — HTML, разбор `backendError`
  // ниже даёт пустоту и сообщение вырождается в «Повторите попытку», хотя
  // повтор бесполезен. Отвечаем по существу.
  if (Number(candidate?.response?.status) === 413) {
    return (
      'Запрос отклонён по размеру, до обработки он не дошёл. '
      + `Лимит на один макет — ${formatUploadSize(MAX_DESIGN_UPLOAD_BYTES)}: `
      + 'уберите файлы тяжелее и повторите.'
    )
  }

  if (isBulkCreateOutcomeUnknown(error)) {
    const code = typeof candidate?.code === 'string' ? candidate.code.toUpperCase() : ''
    const message = typeof candidate?.message === 'string' ? candidate.message : ''
    const reason = (
      code === 'ECONNABORTED'
      || code === 'ETIMEDOUT'
      || /timeout/i.test(message)
    )
      ? 'Превышено время ожидания ответа сервера.'
      : 'Сервер не ответил.'
    const jobHint = jobId ? ` Идентификатор задачи: ${jobId}.` : ''
    return (
      `${reason} Сервер не подтвердил результат запуска: задача могла продолжить выполняться в фоне.${jobHint} `
      + 'Закройте визард и сначала проверьте фоновые задачи и каталог. '
      + 'Новый запуск допустим только после этой проверки.'
    )
  }
  if (backendError) {
    const parts: string[] = []
    if (typeof backendError.message === 'string' && backendError.message.trim()) {
      parts.push(backendError.message.trim())
    }
    if (typeof backendError.code === 'string' && backendError.code.trim()) {
      parts.push(`Код: ${backendError.code.trim()}`)
    }
    const details = formatBackendDetails(backendError.details)
    if (details) parts.push(`Детали: ${details}`)
    if (parts.length > 0) return parts.join('\n')
  }
  return 'Не удалось запустить создание макетов. Повторите попытку.'
}

export function bulkCreateFailureJobState(error: unknown, jobId: string) {
  const outcomeUnknown = isBulkCreateOutcomeUnknown(error)
  const message = bulkCreateErrorMessage(error, jobId)
  return {
    status: 'failed' as const,
    currentStep: outcomeUnknown
      ? 'Ответ сервера не получен — проверьте фоновые задачи и каталог'
      : 'Запуск отклонён сервером',
    error: message,
    completedAt: new Date(),
    metadata: {
      outcomeUnknown,
      checkTasksBeforeNewAttempt: outcomeUnknown,
      submissionLocked: outcomeUnknown,
      jobId,
    },
  }
}

export function isBulkCreateSubmitLocked(unknownAttemptJobId: string | null): boolean {
  return Boolean(unknownAttemptJobId)
}

export function isCurrentPreflight(
  current: { sequence: number; fingerprint: string },
  sequence: number,
  fingerprint: string,
): boolean {
  return current.sequence === sequence && current.fingerprint === fingerprint
}

export function isBackwardWizardNavigation(currentStep: number, targetStep: number): boolean {
  return targetStep < currentStep
}

export function buildFunnelPlanFingerprint(input: {
  commonFields: Record<string, unknown>
  templates: Array<{
    template_key?: string
    pdfFileName?: string
    design_type?: string
    name_on_image?: string
    age_on_image?: string
    age_on_image_unit?: string
  }>
  targets: Array<{
    marketplace?: string
    seller_id?: number
    cabinet?: string
  }>
}): string {
  return JSON.stringify({
    commonFields: Object.fromEntries(
      Object.entries(input.commonFields).sort(([left], [right]) => left.localeCompare(right)),
    ),
    templates: input.templates.map((template) => ({
      template_key: template.template_key || '',
      pdfFileName: template.pdfFileName || '',
      design_type: template.design_type || '',
      name_on_image: template.name_on_image || '',
      age_on_image: template.age_on_image || '',
      age_on_image_unit: template.age_on_image_unit || '',
    })),
    targets: input.targets
      .map((target) => ({
        marketplace: String(target.marketplace || '').trim().toLowerCase(),
        seller_id: Number(target.seller_id || 0),
        cabinet: String(target.cabinet || '').trim().toLowerCase(),
      }))
      .sort((left, right) => (
        `${left.marketplace}:${left.seller_id}:${left.cabinet}`
          .localeCompare(`${right.marketplace}:${right.seller_id}:${right.cabinet}`)
      )),
  })
}

export function buildSeoReadinessContexts(input: {
  rows: FunnelPlanRow[]
  commonFields: Record<string, unknown> & {
    recipient_statuses?: string[]
    personas?: string[]
  }
  templates: Array<{
    template_key: string
    design_type: string
    name_on_image?: string
    age_on_image?: string
    age_on_image_unit?: string
  }>
}): SeoPipelineReadinessContext[] {
  const templateByKey = new Map(input.templates.map((template) => [template.template_key, template]))
  return input.rows.flatMap((row) => (row.product_articles || []).map((futureProduct) => {
    const template = templateByKey.get(row.template_key)
    const recipientStatuses = input.commonFields.recipient_statuses || []
    const personas = input.commonFields.personas || []
    return {
      context_id: [
        row.template_key,
        row.category_prefix,
        row.marketplace,
        row.seller_id,
        row.cabinet,
        futureProduct.quantity_index,
        futureProduct.suffix || '-',
      ].join(':'),
      category_prefix: row.category_prefix,
      design_type: row.design_type,
      design: {
        ...input.commonFields,
        design_type: row.design_type,
        type: row.design_type,
        recipients: recipientStatuses.join(';'),
        recipient_statuses: recipientStatuses.join(';'),
        persona: personas.join(';'),
        personas: personas.join(';'),
        // `name_on_image` is persisted for the design workflow, but the SEO
        // catalog query does not select it. Sending it as recipe context would
        // let readiness approve a path that generateBatch rejects as unknown.
        ...(template?.age_on_image ? { age_on_image: Number(template.age_on_image) || template.age_on_image } : {}),
        ...(template?.age_on_image_unit ? { age_on_image_unit: template.age_on_image_unit } : {}),
      },
      category: {
        category_id: row.category_id,
        prefix: row.category_prefix,
        description: row.category_name || '',
      },
      product: {
        category_id: row.category_id,
        quantity_index: futureProduct.quantity_index,
        article_suffix: futureProduct.suffix,
      },
    }
  }))
}

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, entry]) => [key, stableValue(entry)]),
  )
}

/** Binds an SEO result to the exact plan and future recipe contexts it checked. */
export function buildSeoReadinessFingerprint(input: {
  planFingerprint: string
  contexts: SeoPipelineReadinessContext[]
}): string {
  return JSON.stringify({
    planFingerprint: input.planFingerprint,
    contexts: [...input.contexts]
      .sort((left, right) => left.context_id.localeCompare(right.context_id))
      .map((context) => stableValue(context)),
  })
}

export interface GroupedSeoReadinessMissing {
  kind: string
  title: string
  slot: string
  keys: string[]
  sellers: string[]
  categoryPrefixes: string[]
  designTypes: string[]
  affectedContexts: number
}

export function groupSeoReadinessMissing(
  missing: SeoPipelineReadinessMissing[],
): GroupedSeoReadinessMissing[] {
  const groups = new Map<string, {
    kind: string
    title: string
    slot: string
    keys: Set<string>
    sellers: Set<string>
    categoryPrefixes: Set<string>
    designTypes: Set<string>
    affectedContexts: number
  }>()
  for (const row of missing) {
    const key = `${row.kind}\u0001${row.kind_title}\u0001${row.slot}`
    const group = groups.get(key) || {
      kind: row.kind,
      title: row.kind_title || row.kind,
      slot: row.slot,
      keys: new Set<string>(),
      sellers: new Set<string>(),
      categoryPrefixes: new Set<string>(),
      designTypes: new Set<string>(),
      affectedContexts: 0,
    }
    group.keys.add(`${row.key1}${row.key2 ? ` / ${row.key2}` : ''}`)
    group.sellers.add(`${row.marketplace.toUpperCase()}/${row.seller_id}`)
    row.category_prefixes.forEach((value) => group.categoryPrefixes.add(value))
    row.design_types.forEach((value) => group.designTypes.add(value))
    group.affectedContexts += row.affected_contexts
    groups.set(key, group)
  }
  return [...groups.values()]
    .map((group) => ({
      kind: group.kind,
      title: group.title,
      slot: group.slot,
      keys: [...group.keys].sort((left, right) => left.localeCompare(right, 'ru')),
      sellers: [...group.sellers].sort(),
      categoryPrefixes: [...group.categoryPrefixes].sort(),
      designTypes: [...group.designTypes].sort((left, right) => left.localeCompare(right, 'ru')),
      affectedContexts: group.affectedContexts,
    }))
    .sort((left, right) =>
      left.title.localeCompare(right.title, 'ru') || left.slot.localeCompare(right.slot, 'ru'))
}

export function isPartialSeoPipelineWarning(run: {
  status?: string
  error?: string | null
  pipeline_error?: string | null
}): boolean {
  const message = String(run.error || run.pipeline_error || '')
  return run.status === 'ready' && message.startsWith('SEO_TEXT_PARTIAL:')
}

export type TemplatePersonalizationFields = {
  name_on_image: string
  age_on_image: string
  age_on_image_unit: string
}

/**
 * Поля персонализации необязательны: пустые значения не должны превращаться
 * в отдельные ключи запроса и затем в пустые строки в base_designs.
 */
export function templatePersonalizationPayload(
  row: TemplatePersonalizationFields,
): Partial<TemplatePersonalizationFields> {
  const name = row.name_on_image.trim()
  const age = row.age_on_image.trim()
  const unit = row.age_on_image_unit.trim()

  return {
    ...(name ? { name_on_image: name } : {}),
    ...(age ? { age_on_image: age } : {}),
    ...(unit ? { age_on_image_unit: unit } : {}),
  }
}

/**
 * Строка мастера бывает двух видов: загруженный PDF (`file`) и лист генератора
 * (`fileName`/`sizeBytes`). Общие проверки читают имя и размер только отсюда.
 */
export interface WizardRowFileSource {
  file?: { name: string; size?: number }
  fileName?: string
  sizeBytes?: number
}

/** Имя файла строки — для сообщений об ошибках и отпечатков плана воронок. */
export function rowFileName(row: WizardRowFileSource): string {
  return row.file?.name ?? row.fileName ?? 'файл без имени'
}

/** Размер файла строки в байтах (0 — неизвестен). */
export function rowFileSize(row: WizardRowFileSource): number {
  return Number(row.file?.size ?? row.sizeBytes) || 0
}

/** Файлы, где возраст введён, но его единица не выбрана. */
export function ageWithoutUnitFileNames(
  rows: Array<Pick<TemplatePersonalizationFields, 'age_on_image' | 'age_on_image_unit'> & WizardRowFileSource>,
): string[] {
  return rows
    .filter((row) => row.age_on_image.trim() !== '' && row.age_on_image_unit.trim() === '')
    .map((row) => rowFileName(row))
}

export function invalidDesignTypeFileNames(
  rows: Array<{ design_type: string } & WizardRowFileSource>,
  designTypes: string[],
): string[] {
  const allowed = new Set(designTypes)
  return rows
    .filter((row) => row.design_type && !allowed.has(row.design_type))
    .map((row) => rowFileName(row))
}

/**
 * Площадка мастера из значения `mp_sellers.marketplace`/`seller.mp`.
 *
 * Коды и алиасы берутся из реестра (`normalizeMarketplace`) — своего списка
 * алиасов здесь не заводим, иначе площадка, добавленная в реестр, молча не
 * подхватилась бы тут. Единственное исключение — `site`: канал сайта не
 * ходит через транспорт площадки, поэтому реестр его не знает вовсе.
 */
export function normalizePipelineMarketplace(value: unknown): PipelineMarketplace | null {
  if (String(value ?? '').trim().toLowerCase() === 'site') return 'site'
  return normalizeMarketplace(value)
}

/**
 * Кабинет площадки предлагается целью полного пайплайна, только если
 * публикация с этой площадки включена в реестре
 * (`capabilities.publish`, `frontend/src/shared/marketplaces.ts`). Кабинет
 * узнанной, но пока не подключённой площадки — не «неизвестный маркетплейс»
 * (`normalizePipelineMarketplace` уже вернула бы `null`), а просто временно не
 * выбирается целью: явный отказ по фиче-флагу, а не молчаливое исключение.
 * `site` — цель всегда: у сайта есть кабинет-продавец (`site_pt` в
 * `mp_sellers`), а флаг реестра `publish` его не касается — реестр сайт не
 * знает, каталог на витрину увозит собственный обмен, а не транспорт площадки.
 */
export function isPipelineTargetMarketplace(marketplace: PipelineMarketplace): boolean {
  if (marketplace === 'site') return true
  return marketplaceSupports(marketplace, 'publish')
}

/**
 * Запуски пайплайна (design_pipeline_runs) появляются только в полном режиме и
 * только если бэкенд подтвердил старт. В режиме «только макеты» бэкенд пишет
 * pipeline_launch_status='not_requested' и пустой pipeline_runs — значит ждать
 * номера запусков и показывать этапы товаров/визуалов/карточек нельзя.
 * Пока задача идёт, статус запуска ещё не записан — в полном режиме ждём.
 */
export function expectsPipelineRuns(input: {
  pipelineMode: string
  launchStatus?: unknown
}): boolean {
  if (!launchesPipelineRun(input.pipelineMode)) return false
  const launchStatus = String(input.launchStatus ?? '').trim()
  return launchStatus !== 'not_requested' && launchStatus !== 'not_started'
}

/**
 * Выполнение закончено, когда завершилась корневая задача и либо запусков не
 * ждём вовсе, либо все ожидаемые запуски терминальны. Без проверки expectsRuns
 * экран навсегда застревал в промежуточном состоянии: в режиме «только макеты»
 * запусков не будет никогда, а в полном режиме их может не быть из-за ошибки
 * старта — тогда опрос уже остановлен, а спиннеры продолжали крутиться.
 */
export function isExecutionFinished(input: {
  rootStatus: string
  expectsRuns: boolean
  runs: Array<{ terminal: boolean }>
}): boolean {
  if (input.rootStatus !== 'completed' && input.rootStatus !== 'failed') return false
  if (!input.expectsRuns) return true
  return input.runs.length > 0 && input.runs.every((run) => run.terminal)
}

export type ExecutionStageCard = {
  key?: string
  label: string
  current: number
  total: number
  determinate?: boolean
  failed?: number
  skipped?: number
  /** Only the first unfinished stage is active; future work never pretends to run. */
  state: 'done' | 'running' | 'idle' | 'failed'
}

export function buildExecutionStageCards(input: {
  expectsRuns: boolean
  finished: boolean
  designsCreated: number
  designsTotal: number
  totals: {
    products: number
    productsReady?: number
    production: number
    targets: number
    visuals: number
    seo?: number
    cards: number
    terminalRuns?: number
    expectedRuns?: number
  }
  /**
   * Ждём ли этапы маркетплейсов. В режиме «Макеты и товары» запуски есть,
   * а визуалов и карточек не будет — их плитки показывать нельзя, иначе они
   * навсегда останутся пустыми.
   */
  expectsMarketplace?: boolean
}): ExecutionStageCard[] {
  const expectsMarketplace = input.expectsMarketplace ?? input.expectsRuns
  const stages: Array<Omit<ExecutionStageCard, 'state'>> = [
    { key: 'preparation', label: 'Подготовка задания', current: 1, total: 1, determinate: true },
    { key: 'layouts', label: 'Макеты', current: input.designsCreated, total: input.designsTotal, determinate: true },
    ...(input.expectsRuns
      ? [
          { key: 'products', label: 'Товары', current: input.totals.productsReady ?? 0, total: input.totals.products, determinate: input.totals.products > 0 || input.finished },
          { key: 'production', label: 'Файлы печати', current: input.totals.production, total: input.totals.products, determinate: input.totals.products > 0 || input.finished },
        ]
      : []),
    ...(input.expectsRuns && expectsMarketplace
      ? [
          { key: 'visuals', label: 'Визуалы МП', current: input.totals.visuals, total: input.totals.targets, determinate: input.totals.targets > 0 || input.finished },
          { key: 'seo', label: 'SEO-контент', current: input.totals.seo ?? 0, total: input.totals.targets, determinate: input.totals.targets > 0 || input.finished },
          { key: 'cards', label: 'Черновики карточек МП', current: input.totals.cards, total: input.totals.targets, determinate: input.totals.targets > 0 || input.finished },
        ]
      : []),
    ...(input.expectsRuns
      ? [{
          key: 'finalization',
          label: 'Финальная проверка',
          current: input.totals.terminalRuns ?? 0,
          total: input.totals.expectedRuns ?? 0,
          determinate: (input.totals.expectedRuns ?? 0) > 0 || input.finished,
        }]
      : []),
  ]
  let activeAssigned = false
  return stages.map((stage) => {
    const done = stage.determinate !== false && stage.total > 0 && stage.current >= stage.total
    if (done) return { ...stage, state: 'done' as const }
    if (!input.finished && !activeAssigned) {
      activeAssigned = true
      return { ...stage, state: 'running' as const }
    }
    return { ...stage, state: 'idle' as const }
  })
}

export function executionHeadline(input: {
  expectsRuns: boolean
  finished: boolean
  failed: boolean
  expectsMarketplace?: boolean
}): { title: string; description: string | null } {
  const expectsMarketplace = input.expectsMarketplace ?? input.expectsRuns
  if (!input.finished) {
    return {
      title: input.expectsRuns ? 'Пайплайн выполняется' : 'Создаём макеты',
      description: null,
    }
  }
  if (input.failed) {
    return {
      title: input.expectsRuns ? 'Пайплайн завершён с ошибками' : 'Макеты созданы не полностью',
      description: input.expectsRuns
        ? 'Часть этапов завершилась с ошибками — подробности показаны ниже.'
        : 'Часть макетов создать не удалось — подробности показаны ниже.',
    }
  }
  return {
    title: input.expectsRuns ? 'Пайплайн успешно завершён' : 'Макеты созданы',
    description: !input.expectsRuns
      ? 'Выбран режим «только макеты»: товары и карточки маркетплейсов не запрашивались.'
      : expectsMarketplace
        ? 'Все выбранные этапы успешно завершены.'
        : 'Макеты, товары и файлы печати готовы. Карточки маркетплейсов не запрашивались.',
  }
}

export function canSubmitFullPipeline(input: {
  targetCount: number
  sellersReady: boolean
  designTypesReady: boolean
  preflightReady: boolean
}): boolean {
  return (
    input.targetCount > 0
    && input.sellersReady
    && input.designTypesReady
    && input.preflightReady
  )
}

/** Типы макетов из «Матрицы категорий» (fallback, если справочник пуст). */
export const FALLBACK_DESIGN_TYPES = [
  'Круг', 'Топпер', 'Капк 5', 'Капк 6', 'Капк 8', 'Квадрат', 'Эклеры', 'Паттерны', 'Фигурный декор', 'Бенто',
]

/**
 * Общие поля тематики мастера. Бренд и тематика — одна обязательность на двоих,
 * персона и статус получателя — мульти-атрибуты (предзаполняются из «пол × возраст»).
 */
export interface WizardCommonFields {
  category: string
  theme: string
  brand_ru: string
  gender: string
  age: string
  occasion: string
  recipient_statuses: string[]
  personas: string[]
}

export const EMPTY_WIZARD_COMMON: WizardCommonFields = {
  category: '',
  theme: '',
  brand_ru: '',
  gender: '',
  age: '',
  occasion: '',
  recipient_statuses: [],
  personas: [],
}

/**
 * Единица возраста на картинке по числу (SPEC_2026-10-03 §3.5): 1 -> «год»,
 * 2-4 -> «года», остальное -> «лет», с исключением 11-14. Пусто или не целое число -> пусто:
 * мастер тогда попросит единицу на шаге «Указание типов».
 */
export function ageOnImageUnit(age: unknown): string {
  const text = String(age ?? '').trim()
  if (!/^\d+$/.test(text)) return ''
  const value = Number.parseInt(text, 10)
  const lastTwo = value % 100
  const last = value % 10
  if (last === 1 && lastTwo !== 11) return 'год'
  if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return 'года'
  return 'лет'
}

/** Справочники атрибутов мастера (`attribute_values`), по которым проверяются значения из генератора. */
export interface WizardAttributeDictionaries {
  category?: string[]
  gender?: string[]
  age?: string[]
  occasion?: string[]
  persona?: string[]
  recipient_status?: string[]
}

export interface UnknownDictionaryValue {
  field: 'category' | 'gender' | 'age' | 'occasion' | 'personas' | 'recipient_statuses'
  label: string
  value: string
}

const UNKNOWN_VALUE_CHECKS: Array<{
  field: UnknownDictionaryValue['field']
  label: string
  dictionary: keyof WizardAttributeDictionaries
}> = [
  { field: 'category', label: 'Категория', dictionary: 'category' },
  { field: 'gender', label: 'Пол аудитории', dictionary: 'gender' },
  { field: 'age', label: 'Возраст аудитории', dictionary: 'age' },
  { field: 'occasion', label: 'Повод', dictionary: 'occasion' },
  { field: 'recipient_statuses', label: 'Статус получателя', dictionary: 'recipient_status' },
  { field: 'personas', label: 'Персона', dictionary: 'persona' },
]

/**
 * Значения общих полей, которых нет в справочнике `attribute_values` (SPEC §3.3 п.3): мастер не теряет
 * их молча, а подсвечивает. Бренд не проверяется — новый бренд заводится в справочник при создании.
 * Пустой справочник (не загрузился) ложных срабатываний не даёт.
 */
export function unknownDictionaryValues(
  common: Pick<WizardCommonFields, 'category' | 'gender' | 'age' | 'occasion' | 'recipient_statuses' | 'personas'>,
  dictionaries: WizardAttributeDictionaries,
): UnknownDictionaryValue[] {
  const unknown: UnknownDictionaryValue[] = []
  for (const check of UNKNOWN_VALUE_CHECKS) {
    const dictionary = dictionaries[check.dictionary]
    if (!dictionary || dictionary.length === 0) continue
    const known = new Set(dictionary)
    const raw = common[check.field]
    const values = Array.isArray(raw) ? raw : [raw]
    for (const value of values) {
      const text = String(value ?? '').trim()
      if (text && !known.has(text)) unknown.push({ field: check.field, label: check.label, value: text })
    }
  }
  return unknown
}

/**
 * Общие поля мастера из ответа `catalog-draft`. Пустые персона и статус получателя у задания,
 * у которого тематики ещё нет, выводятся из пары «пол × возраст» ровно как при ручном вводе
 * (SPEC §3.3 п.4); непустые значения генератора не трогаются. Закреплённые за тематикой поля
 * не достраиваются.
 */
export function wizardCommonFromDraft(
  fields: WizardCommonFields,
  locked: boolean,
  dictionaries: WizardAttributeDictionaries,
): WizardCommonFields {
  const base: WizardCommonFields = {
    ...fields,
    recipient_statuses: [...fields.recipient_statuses],
    personas: [...fields.personas],
  }
  if (locked || !base.gender.trim() || !base.age.trim()) return base
  if (base.personas.length > 0 && base.recipient_statuses.length > 0) return base
  const derived = deriveAudienceAttributes(base, 'age', base.age, {
    persona: dictionaries.persona,
    recipient_status: dictionaries.recipient_status,
  })
  return {
    ...base,
    personas: base.personas.length > 0 ? base.personas : derived.personas,
    recipient_statuses: base.recipient_statuses.length > 0 ? base.recipient_statuses : derived.recipient_statuses,
  }
}

/** Понятные оператору причины отказа листа (коды совпадают с `assertSubmittable` на сервере). */
const BLOCKER_TEXTS: Record<string, string> = {
  NOT_CURRENT: 'это не текущая версия листа, пересоберите серию',
  NOT_READY: 'лист ещё не готов',
  TEMPLATE_NOT_ACTIVE: 'Шаблон листа не откалиброван',
  VALIDATION_NOT_PASSED: 'не пройдена техническая проверка листа',
  FILES_INCOMPLETE: 'у листа нет PDF или превью',
  ALREADY_SUBMITTED: 'лист уже отправлен в каталог',
  NOT_APPROVED: 'лист не утверждён в студии, утвердите серию перед отправкой',
}

/**
 * Причины, которые мастер действительно показывает. «Не утверждён в студии» снимается, когда мастер
 * сам утверждает серию перед созданием (кнопка студии «Утвердить и отправить в ПИМ»).
 */
export function effectiveDraftBlockers<T extends { code: string }>(blockers: T[], approveSeries: boolean): T[] {
  return approveSeries ? blockers.filter((blocker) => blocker.code !== 'NOT_APPROVED') : blockers
}

export function blockerReasonText(blocker: { code: string; message: string }): string {
  return BLOCKER_TEXTS[blocker.code] || blocker.message || 'лист нельзя отправить в каталог'
}

/**
 * Стартовый шаг мастера в режиме «из генератора» (SPEC §3.4), первое подходящее условие:
 * неготовые листы -> 1; незаполненные или не из справочника поля -> 2; иначе -> 4.
 */
export function wizardStartStep(input: {
  blockerCount: number
  missingFieldCount: number
  unknownValueCount: number
}): 1 | 2 | 4 {
  if (input.blockerCount > 0) return 1
  if (input.missingFieldCount > 0 || input.unknownValueCount > 0) return 2
  return 4
}

/** Строка мастера для листа генератора: файла нет, есть ревизия. */
export interface AiRevisionRowDraft {
  revisionId: string
  fileName: string
  design_type: string
  name_on_image: string
  age_on_image: string
  age_on_image_unit: string
}

/**
 * Шаблон запроса создания для листа генератора (контракт К3): `template_key` = id ревизии,
 * `ai_layout_revision_id` обязателен, `upload_id` и `pdfFile` запрещены.
 */
export function aiTemplatePayload(row: AiRevisionRowDraft) {
  return {
    template_key: row.revisionId,
    ai_layout_revision_id: row.revisionId,
    pdfFileName: row.fileName,
    design_type: row.design_type,
    ...templatePersonalizationPayload(row),
  }
}
